<template>
  <div>
    <h2 class="heading-2 mb-24 pl-24">{{ i18n(translations.title) }}</h2>
    <RevList>
      <RevListItemInteractive
        v-for="(step, index) in filteredSteps"
        :key="step.value"
        class="p-0"
        :to="{ name: getRouteNameForStep(step.value, index) }"
      >
        <template v-if="ICONS[step.status]" #prefix>
          <component
            :is="ICONS[step.status]?.component"
            :color="ICONS[step.status]?.color"
          />
        </template>
        <template #label>
          <span :class="isStepActive(index) ? 'body-1-bold' : 'body-1'">
            {{ step.label }}
          </span>
        </template>
        <template #suffix>
          <IconChevronRight />
        </template>
      </RevListItemInteractive>
    </RevList>
    <RevButton
      class="mt-24 w-full"
      :disabled="!allStepsDone"
      :to="{ name: ROUTE_NAMES.KYB.STEPS.REVIEW }"
      variant="primary"
    >
      {{ i18n(translations.reviewStepLabel) }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { IconCheckInCircleFilled } from '@ds/icons/IconCheckInCircleFilled'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'

import type { KybStep } from '~/api/kyb/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useKybData } from '~/scopes/kyb/composables/useKybData'

import { KYB_STEP_CODES, KYB_STEP_STATUS } from './Stepper.constants'
import translations from './Stepper.translations'

const { steps } = useKybData()
const route = useRoute()

const ICONS = {
  [KYB_STEP_STATUS.TODO]: null,
  [KYB_STEP_STATUS.DONE]: {
    component: IconCheckInCircleFilled,
    color: 'green',
  },
  [KYB_STEP_STATUS.ERROR]: {
    component: IconCrossInCircle,
    color: 'red',
  },
}

const i18n = useI18n()

const filteredSteps = computed<KybStep[]>(() => {
  return steps.value
    .filter((step) => KYB_STEP_CODES.includes(step.value))
    .sort(
      (a, b) =>
        KYB_STEP_CODES.indexOf(a.value) - KYB_STEP_CODES.indexOf(b.value),
    )
})

const allStepsDone = computed(() => {
  return (
    filteredSteps.value.length > 0 &&
    filteredSteps.value.every((step) => step.status === KYB_STEP_STATUS.DONE)
  )
})

function getRouteNameForStep(stepCode: string, index: number): string {
  return ROUTE_NAMES.KYB.STEPS[
    `STEP_${index + 1}` as keyof typeof ROUTE_NAMES.KYB.STEPS
  ]
}

function isStepActive(index: number): boolean {
  const stepRouteName = getRouteNameForStep('', index)

  return route.name === stepRouteName
}
</script>
